import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Download } from "lucide-react";
import { format } from "date-fns";

const WaitlistDashboard = () => {
  const { requireAdmin } = useAdmin();
  requireAdmin();

  const [searchQuery, setSearchQuery] = useState("");
  const [profileFilter, setProfileFilter] = useState<string>("all");

  const { data: waitlistEntries, isLoading } = useQuery({
    queryKey: ["admin-waitlist"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("waitlist")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const filteredEntries = waitlistEntries?.filter((entry) => {
    const matchesSearch =
      entry.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.company_or_organization?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      profileFilter === "all" ||
      (profileFilter === "completed" && entry.completed_profile) ||
      (profileFilter === "pending" && !entry.completed_profile);

    return matchesSearch && matchesFilter;
  });

  const exportToCSV = () => {
    if (!filteredEntries || filteredEntries.length === 0) return;

    const headers = [
      "First Name",
      "Last Name",
      "Email",
      "Company",
      "Role",
      "Industry",
      "Signup As",
      "Created At",
      "Profile Completed",
    ];

    const rows = filteredEntries.map((entry) => [
      entry.first_name,
      entry.last_name,
      entry.email,
      entry.company_or_organization || "",
      entry.role || "",
      entry.industry || "",
      entry.signup_as,
      format(new Date(entry.created_at), "yyyy-MM-dd HH:mm:ss"),
      entry.completed_profile ? "Yes" : "No",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `waitlist-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto py-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Waitlist Dashboard</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
              <Select value={profileFilter} onValueChange={setProfileFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entries</SelectItem>
                  <SelectItem value="completed">Profile Completed</SelectItem>
                  <SelectItem value="pending">Profile Pending</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={exportToCSV} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Profile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries && filteredEntries.length > 0 ? (
                    filteredEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {entry.first_name} {entry.last_name}
                        </TableCell>
                        <TableCell>{entry.email}</TableCell>
                        <TableCell>{entry.company_or_organization || "—"}</TableCell>
                        <TableCell>{entry.role || "—"}</TableCell>
                        <TableCell>{entry.industry || "—"}</TableCell>
                        <TableCell>
                          {format(new Date(entry.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          <span
                            className={
                              entry.completed_profile
                                ? "text-green-600"
                                : "text-yellow-600"
                            }
                          >
                            {entry.completed_profile ? "Completed" : "Pending"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center">
                        No waitlist entries found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="text-sm text-muted-foreground">
              Showing {filteredEntries?.length || 0} of {waitlistEntries?.length || 0}{" "}
              entries
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WaitlistDashboard;
